import { Button } from '@/components/ui/button';
import { Phone, Trash2, Star, X, CheckCircle, PhoneOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ReasonTooltip } from '@/components/shared/ReasonTooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface BulkActionsProps {
  selectedCount: number;
  onCall: () => void;
  onChangePriority: () => void;
  onChangeStatus: (status: 'active' | 'do_not_call') => void;
  /** Absent for a caller: deleting prospects is for tenant admins (AUDIT-F23). */
  onDelete?: () => void;
  onDoNotCall: () => void;
  onClear: () => void;
  /** QA-W7-2: why the edits and the delete are off (permission removed), '' when allowed. */
  writeBlockedReason?: string;
}

export default function BulkActions({
  selectedCount,
  onCall,
  onChangePriority,
  onChangeStatus,
  onDelete,
  onDoNotCall,
  onClear,
  writeBlockedReason = '',
}: BulkActionsProps) {
  const blocked = !!writeBlockedReason;
  const { t } = useTranslation(['dialer', 'common']);
  if (selectedCount === 0) return null;

  // #10 -- ce bandeau etait `fixed bottom-6 left-1/2` : un calque pose en bas
  // au centre, qui recouvrait la barre d'appel (chevauchement mesure :
  // 632 x 32 px). Le client le veut en haut, sur la ligne des reglages. Rendu
  // INLINE, il prend sa place dans le flux : plus aucune superposition
  // possible, quelle que soit la hauteur de la fenetre.
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.98 }}
        className="shrink-0"
        data-testid="bulk-actions"
      >
        <div className="bg-white text-gray-900 rounded-lg shadow-sm px-2 py-1.5 flex items-center gap-2 border border-gray-300">
          <Badge className="bg-[var(--vf-primary)] text-white px-2 py-0.5 whitespace-nowrap">
            {t('dialer:bulkActions.selected', { count: selectedCount })}
          </Badge>

          <div className="flex gap-1.5">
            <Button size="sm" onClick={onCall} className="bg-green-600 hover:bg-green-700 text-white">
              <Phone className="w-4 h-4 mr-2" />
              {t('common:actions.call')}
            </Button>

            <ReasonTooltip reason={writeBlockedReason}>
            <Button
              size="sm"
              variant="outline"
              onClick={onChangePriority}
              disabled={blocked}
            >
              <Star className="w-4 h-4 mr-2" />
              {t('dialer:bulkActions.priority')}
            </Button>
            </ReasonTooltip>

            <ReasonTooltip reason={writeBlockedReason}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild disabled={blocked}>
                <Button size="sm" variant="outline" disabled={blocked}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {t('common:field.status')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => onChangeStatus('active')}>{t('dialer:bulkActions.reactivate')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onChangeStatus('do_not_call')}>{t('dialer:bulkActions.doNotCallItem')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </ReasonTooltip>

            <ReasonTooltip reason={writeBlockedReason}>
            <Button
              size="sm"
              variant="outline"
              onClick={onDoNotCall}
              disabled={blocked}
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
            >
              <PhoneOff className="w-4 h-4 mr-2" />
              {t('dialer:bulkActions.doNotCallButton')}
            </Button>
            </ReasonTooltip>

            {onDelete && (
              <ReasonTooltip reason={writeBlockedReason}>
              <Button
                size="sm"
                variant="outline"
                onClick={onDelete}
                disabled={blocked}
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('common:actions.delete')}
              </Button>
              </ReasonTooltip>
            )}
          </div>

          <Button size="sm" variant="ghost" onClick={onClear} className="text-gray-500 hover:text-gray-900"
            aria-label={t('dialer:bulkActions.clearSelection')} title={t('dialer:bulkActions.clearSelection')}>
            <X className="w-4 h-4" aria-hidden />
          </Button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
